import { TranslateService } from '@ngx-translate/core';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { UserRegister } from '../models/user-register.model';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { AuthTokens } from '../models/auth-tokens.model';

interface DecodedToken {
  sub: string;
  name: string;
  role?: string;
  exp: number;
  userId: number;
  status: string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private readonly emailLanguage = inject(TranslateService);
  private get requestLanguage(): string { return this.emailLanguage.getCurrentLang() === 'en' ? 'en' : 'ar'; }

  private apiUrl = environment.apiUrl + '/api/Auth';

  private ACCESS_TOKEN_KEY = 'accessToken';
  private REFRESH_TOKEN_KEY = 'refreshToken';
  private REFRESH_BUFFER_MS = 60 * 1000; // 1 minute

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  redirectUrl: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        if (!event.url.includes('/login') && !event.url.includes('/register')) {
          // Never retain an email capability token in the login return URL.
          this.redirectUrl = event.url.startsWith('/saved-searches/unsubscribe') ? '/saved-searches/unsubscribe' : event.url;
        }
      });
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  storeTokens(tokens: AuthTokens): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  login(credentials: { email: string; password: string }): Observable<AuthTokens> {
    return this.http.post<AuthTokens>(`${this.apiUrl}/login`, credentials);
  }

  refreshToken(): Observable<AuthTokens> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap(() => {
          return of({
            accessToken: this.getAccessToken()!,
            refreshToken: this.getRefreshToken()!,
          });
        })
      );
    }

    this.isRefreshing = true;

    return this.http
      .post<AuthTokens>(`${this.apiUrl}/refresh`, {
        refreshToken: this.getRefreshToken(),
      })
      .pipe(
        tap((tokens) => {
          this.storeTokens(tokens);
          this.refreshTokenSubject.next(tokens.accessToken);
          this.isRefreshing = false;
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.logout();
          return throwError(() => err);
        })
      );
  }

  getTokenExpiration(token: string): number | null {
    const payload = this.decodeToken(token);
    return payload?.exp ? payload.exp * 1000 : null;
  }

  isTokenExpired(token: string): boolean {
    const expiry = this.getTokenExpiration(token);
    return !expiry || Date.now() > expiry;
  }

  shouldRefreshToken(token: string): boolean {
    const expiry = this.getTokenExpiration(token);
    if (!expiry) return true;

    return Date.now() > expiry - this.REFRESH_BUFFER_MS;
  }

  getValidAccessToken(): Observable<string | null> {
    const token = this.getAccessToken();

    if (!token) {
      return of(null);
    }

    if (this.shouldRefreshToken(token)) {
      return this.refreshToken().pipe(
        map((tokens) => tokens.accessToken),
        catchError(() => of(null))
      );
    }

    return of(token);
  }

  loginWithGmail(credentials: { idToken: string }): Observable<AuthTokens> {
    return this.http.post<AuthTokens>(`${this.apiUrl}/login/google`, credentials);
  }

  loginWithFacebook(credentials: { accessToken: string }): Observable<AuthTokens> {
    return this.http.post<AuthTokens>(`${this.apiUrl}/login/facebook`, credentials);
  }

  logout() {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);

    const rootSnapshot = this.router.routerState.snapshot.root;
    const isProtected = this.routeHasAuthGuard(rootSnapshot);

    if (isProtected) {
      this.router.navigateByUrl('/');
    }
  }

  register(userRegister: UserRegister) {
    return this.http.post(`${this.apiUrl}/register`, {
      email: userRegister.email,
      password: userRegister.password,
      name: userRegister.name,
      phoneNumber: userRegister.phoneNumber,
      termsAccepted: userRegister.termsAccepted,
      language: this.requestLanguage,
    });
  }

  sendCode(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/send-code`, { email, language: this.requestLanguage });
  }

  verifyCode(email: string, code: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/verify-code`, { email, code });
  }

  requestPasswordReset(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/request-password-reset`, { email, language: this.requestLanguage });
  }

  verifyResetToken(token: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/verify-reset-token?token=${token}`);
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/reset-password`, { token, newPassword });
  }

  updatePassword(email: string, oldPassword: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/update-password`, {
      email,
      oldPassword,
      newPassword,
    });
  }

  decodeToken(token: string): DecodedToken | null {
    if (!token) return null;

    try {
      const decoded = jwtDecode<DecodedToken>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      if (decoded.exp <= currentTime) {
        return null;
      }
      return decoded;
    } catch (e) {
      return null;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Recursively check if any route segment has canActivate guards */
  private routeHasAuthGuard(route: ActivatedRouteSnapshot): boolean {
    if (route.routeConfig?.canActivate?.length) {
      return true;
    }

    // Recursively check child routes
    for (const child of route.children) {
      if (this.routeHasAuthGuard(child)) {
        return true;
      }
    }

    return false;
  }
}
